import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { useShownSlicePlane } from '~/hooks/target/useActiveEditTarget';
import { RoiId } from '~/domain/changes/ids';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useStructureSetCommands } from '~/hooks/domain/useStructureSetCommands';
import { useClearContourExtent } from '~/hooks/structure-set/useClearContourExtent';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import {
    multiSelectedRoiNumbersState,
    openedStructureDeletionConfirmationDialogState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import { selectedToolState } from '~/state/atoms/toolsAtoms';
import { isTextEntryElement } from '~/utils/textEntry';

/** The clear-slice shortcut's wording, shared with the viewport menu. */
const SHORTCUT_MESSAGES = {
    successMessageId: 'contourDeletion.extent.success',
    failedMessageId: 'contourDeletion.extent.failed',
    undoLabelId: 'contourDeletion.undo',
    undoUnavailableMessageId: 'contourDeletion.undoUnavailable',
    logLabel: 'Delete contours',
} as const;

const StructureDeletionConfirmationDialog = ({
    roi,
    roiId,
    isSelected,
}: {
    roi: RoiReadModel;
    /** Branded RoiId from the projection layer. When present, deletion is
     * dispatched through StructureSetService (new path). Falls back to the
     * legacy useStructureSet domain-event path when absent. */
    roiId?: RoiId;
    isSelected: boolean;
}) => {
    const { structureSet, deleteRoi } = useStructureSet();
    const { removeRoi } = useStructureSetCommands();
    const { readPlane } = useShownSlicePlane();
    const selectedTool = useRecoilValue(selectedToolState);
    const setSelectedROI = useSetRecoilState<number>(selectedROIState);
    const multiSelected = useRecoilValue(multiSelectedRoiNumbersState);
    const [
        openedStructureDeletionConfirmationDialog,
        setOpenedStructureDeletionConfirmationDialog,
    ] = useRecoilState(openedStructureDeletionConfirmationDialogState);

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    useEffect(() => {
        setIsDialogOpen(
            isSelected &&
                openedStructureDeletionConfirmationDialog === roi.roiNumber
        );
    }, [isSelected, openedStructureDeletionConfirmationDialog, roi.roiNumber]);

    const handleDeleteStructureDialogOpen = useCallback(() => {
        setOpenedStructureDeletionConfirmationDialog(roi.roiNumber);
    }, [roi.roiNumber, setOpenedStructureDeletionConfirmationDialog]);

    // Routed through the shared write rather than calling the engine
    // directly. Three things it gains by that, all of which the direct
    // call lacked: it cannot throw on a main view that cuts no image
    // slice (clearContoursOnSlice does, and nothing here caught it), it
    // REPORTS what it removed, and — since a keystroke commits with no
    // result checked first — its report carries the way back.
    //
    // No confirmation on the keystroke: a shortcut a clinician has
    // learned should not grow a dialog, and the Undo covers the slip.
    const { clearContourExtent } = useClearContourExtent(
        structureSet?.dicomUid,
        SHORTCUT_MESSAGES
    );
    const quickApplyClearContoursOnSliceOperation = useCallback(() => {
        // The plane is read at the keystroke, never captured at render: a
        // scroll between the two must not clear the slice shown earlier.
        void clearContourExtent({
            scope: 'this-slice',
            plane: readPlane(),
            addressedBy: 'acquisition',
            offerUndo: true,
        });
    }, [clearContourExtent, readPlane]);

    useEffect(() => {
        const handleRoiDeletionKeyDown = (event: KeyboardEvent) => {
            // The bulk panel owns the Delete key while a multi-selection is
            // active; suppress the single-ROI variants here.
            if (multiSelected.size >= 2) {
                return;
            }
            if (isTextEntryElement(event.target)) {
                return;
            }
            if (event.key === 'Delete' && isSelected) {
                if (selectedTool === 'brush' || selectedTool === 'line') {
                    quickApplyClearContoursOnSliceOperation();
                } else {
                    if (event.shiftKey) {
                        quickApplyClearContoursOnSliceOperation();
                    } else {
                        handleDeleteStructureDialogOpen();
                    }
                }
            }
        };

        document.addEventListener('keydown', handleRoiDeletionKeyDown);
        return () => {
            document.removeEventListener('keydown', handleRoiDeletionKeyDown);
        };
    }, [
        handleDeleteStructureDialogOpen,
        isSelected,
        multiSelected.size,
        quickApplyClearContoursOnSliceOperation,
        selectedTool,
    ]);

    const handleDeleteStructureConfirmation = useCallback(() => {
        if (isSelected && structureSet) {
            setSelectedROI(-1);
            if (roiId !== undefined) {
                removeRoi(roiId);
            } else {
                deleteRoi(roi.roiNumber);
            }
            setOpenedStructureDeletionConfirmationDialog(null);
        }
    }, [
        isSelected,
        structureSet,
        roiId,
        removeRoi,
        deleteRoi,
        roi.roiNumber,
        setSelectedROI,
        setOpenedStructureDeletionConfirmationDialog,
    ]);

    const handleDeleteStructureDialogCancel = () => {
        setOpenedStructureDeletionConfirmationDialog(null);
    };

    const handleConfirmationDialogKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (
                event.key === 'Enter' &&
                openedStructureDeletionConfirmationDialog === roi.roiNumber
            ) {
                handleDeleteStructureConfirmation();
            }
        },
        [
            handleDeleteStructureConfirmation,
            openedStructureDeletionConfirmationDialog,
            roi.roiNumber,
        ]
    );

    return (
        <ConfirmationDialog
            open={isDialogOpen}
            onCancel={handleDeleteStructureDialogCancel}
            onConfirm={handleDeleteStructureConfirmation}
            title={<FormattedMessage id="structureDeletion" />}
            message={
                <FormattedMessage
                    id="structureDeletionConfirmation"
                    values={{
                        structureName: <strong>{roi.name}</strong>,
                    }}
                />
            }
            cancelText={<FormattedMessage id="cancel" />}
            confirmText={<FormattedMessage id="delete" />}
            onKeydown={handleConfirmationDialogKeyDown}
        />
    );
};

export default StructureDeletionConfirmationDialog;
